/**
 * Global theme behaviour: scroll reveal, sticky header, parallax,
 * navigation loading bar and reduced-motion handling.
 */

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class ScrollReveal {
  constructor() {
    this.targets = document.querySelectorAll('[data-animate]');
    if (!this.targets.length || prefersReducedMotion) {
      this.targets.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    this.observer = new IntersectionObserver((entries) => this.onIntersect(entries), {
      threshold: 0.15,
      rootMargin: '0px 0px -8% 0px',
    });
    this.targets.forEach((el) => this.observer.observe(el));
  }

  onIntersect(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const delay = entry.target.dataset.animateDelay || 0;
      window.setTimeout(() => entry.target.classList.add('is-visible'), Number(delay));
      this.observer.unobserve(entry.target);
    });
  }
}

class StickyHeader {
  constructor() {
    this.header = document.querySelector('[data-sticky-header]');
    if (!this.header) return;
    this.lastY = window.scrollY;
    this.ticking = false;
    window.addEventListener('scroll', () => this.requestTick(), { passive: true });
  }

  requestTick() {
    if (this.ticking) return;
    this.ticking = true;
    window.requestAnimationFrame(() => this.update());
  }

  update() {
    const y = window.scrollY;
    this.header.classList.toggle('is-scrolled', y > 8);
    if (y > this.lastY && y > this.header.offsetHeight + 40) {
      this.header.classList.add('is-hidden');
    } else {
      this.header.classList.remove('is-hidden');
    }
    this.lastY = y;
    this.ticking = false;
  }
}

class HeaderOffset {
  constructor() {
    this.headerGroup = document.getElementById('SiteHeaderGroup');
    if (!this.headerGroup) return;
    this.ticking = false;
    this.update();
    window.addEventListener('resize', () => this.requestTick(), { passive: true });
    window.addEventListener('load', () => this.update());
    if (document.fonts) document.fonts.ready.then(() => this.update());
  }

  requestTick() {
    if (this.ticking) return;
    this.ticking = true;
    window.requestAnimationFrame(() => {
      this.update();
      this.ticking = false;
    });
  }

  update() {
    document.documentElement.style.setProperty('--header-offset', `${this.headerGroup.offsetHeight}px`);
  }
}

class Parallax {
  constructor() {
    this.targets = document.querySelectorAll('[data-parallax]');
    if (!this.targets.length || prefersReducedMotion) return;
    this.startY = window.scrollY;
    this.ticking = false;
    window.addEventListener('scroll', () => this.requestTick(), { passive: true });
    this.update();
  }

  requestTick() {
    if (this.ticking) return;
    this.ticking = true;
    window.requestAnimationFrame(() => this.update());
  }

  update() {
    const viewportH = window.innerHeight;
    const scrolled = window.scrollY - this.startY;
    this.targets.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > viewportH) return;
      const strength = Number(el.dataset.parallaxStrength) || 18;
      el.style.transform = `translateY(${((scrolled / viewportH) * strength).toFixed(2)}%)`;
    });
    this.ticking = false;
  }
}

class BackToTop {
  constructor() {
    this.button = document.querySelector('[data-back-to-top]');
    if (!this.button) return;
    this.button.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
    });
  }
}

class PageLoader {
  constructor() {
    this.el = document.getElementById('Preloader');
    if (!this.el) return;
    this.el.addEventListener('transitionend', () => this.el.remove());
    // Hide as soon as the DOM is ready rather than waiting for every image
    // and font to finish downloading (window's load event) - the page is
    // already usable by then, so there's nothing left worth blocking on.
    this.el.classList.add('is-hidden');
  }
}

class LoadingBar {
  constructor() {
    this.bar = document.getElementById('LoadingBar');
    if (!this.bar) return;
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (!link || link.target === '_blank' || link.hasAttribute('download')) return;
      if (link.origin !== window.location.origin) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey) return;
      this.bar.classList.add('is-loading');
    });
    window.addEventListener('pageshow', () => this.bar.classList.remove('is-loading', 'is-complete'));
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new ScrollReveal();
  new StickyHeader();
  new HeaderOffset();
  new Parallax();
  new BackToTop();
  new PageLoader();
  new LoadingBar();
});
